// src/components/ResourceManager/Hardware/HardwareInventoryPage.tsx

import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import {
	addHardwareResource,
	updateHardwareResource,
	deleteHardwareResource,
	getDataCentersIdsNames,
	getHardwareByDatacenterId,
	getPortsByHardwareId,
	createPort,
	updatePort,
	deletePort,
	getHardwareTypes,
	getSubTypesByTypeHardware,
	DataCenterItem,
	AddHardwareResourcePayload,
	Hardware,
	Port,
	HardwareType,
} from "./request/request";

import { getHardwareListByDataCenter } from "../Port/request/request";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";

import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusV3.svg";
import { getHardwareColumns } from "./HardwareTableColumns";

// ------------------------------------------
// NUEVA INTERFAZ: Para agregar “subType” local en el form.
interface MyHardwareForm extends Omit<AddHardwareResourcePayload, "name"> {
	id?: string;
	typeHardwareId: number | string;
	subtypeHardware: string | { name: string } | null;
	subtypeHardwareId: number | string;
	targetDeviceId?: number | null;
	slotAssignmentId?: number | null;
}
interface HardwareDropdownItem {
	id: number;
	hostname: string;
}
interface PortItem {
	id: number;
	portName: string;
	portNumber: string;
}
// Quitamos “name” y agregamos “subType”
const defaultHwForm: MyHardwareForm = {
	typeHardwareId: "",
	subtypeHardwareId: "",
	datacenterId: "",
	typeHardware: "",
	serialNumber: "",
	model: "",
	vendor: "",
	hostname: "",
	installedDate: "",
	endOfLifeDate: "",
	rackId: "",
	rackUnit: "",
	status: "",
	phase: "",
	template: "",
	subtypeHardware: "",
};

export default function HardwareInventoryPage() {
	// ===== 1) ESTADOS =====
	const [hardwareList, setHardwareList] = useState<Hardware[]>([]);
	const [isFetching, setIsFetching] = useState(false);

	// Form “hwForm”
	const [hwForm, setHwForm] = useState<MyHardwareForm>(defaultHwForm);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);

	// View hardware
	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [selectedHardware, setSelectedHardware] = useState<Hardware | null>(
		null
	);

	// Alert Delete
	const [isAlertOpen, setIsAlertOpen] = useState(false);
	const [hardwareToDelete, setHardwareToDelete] = useState<Hardware | null>(
		null
	);

	// Modal Ports
	const [isPortsModalOpen, setIsPortsModalOpen] = useState(false);
	const [selectedHardwareForPorts, setSelectedHardwareForPorts] =
		useState<Hardware | null>(null);
	const [portsList, setPortsList] = useState<Port[]>([]);

	// Auth0
	const { getAccessTokenSilently, isLoading, isAuthenticated } = useAuth0();

	// DataCenters
	const [dataCenters, setDataCenters] = useState<DataCenterItem[]>([]);
	const [dataCenterOptions, setDataCenterOptions] = useState<string[]>([]);
	const [selectedDataCenterName, setSelectedDataCenterName] = useState("");

	// For "Module" type only:
	const [hostnameList, setHostnameList] = useState<HardwareDropdownItem[]>([]);
	const [hostnameOptions, setHostnameOptions] = useState<string[]>([]);
	const [hostnameId, setHostnameId] = useState<number | null>(null);

	// For slot assignment
	const [slotList, setSlotList] = useState<PortItem[]>([]);
	const [slotOptions, setSlotOptions] = useState<string[]>([]);
	const [slotId, setSlotId] = useState<number | null>(null);

	// ===== 2) NUEVO: “Type” y “SubType” =====
	// Mantenemos la lista entera de TypeHardware con {id, name} para buscar su ID al seleccionar.
	const [typeHardwareList, setTypeHardwareList] = useState<HardwareType[]>([]);
	const typeHardwareOptions = useMemo(
		() => typeHardwareList.map((t) => t.name),
		[typeHardwareList]
	);

	// Subtypes => al escoger un Type, llamamos a getSubTypesByTypeHardware
	const [subTypeList, setSubTypeList] = useState<HardwareType[]>([]);
	const subTypeOptions = useMemo(
		() => subTypeList.map((s) => s.name),
		[subTypeList]
	);

	// ===== 3) EFECTOS =====
	// 3.1) Cargar DataCenters y Types
	useEffect(() => {
		if (isLoading || !isAuthenticated) return;

		(async () => {
			try {
				setIsFetching(true);
				const token = await getAccessTokenSilently();

				// 1) DataCenters
				const respDC = await getDataCentersIdsNames(token);
				if (respDC.data) {
					setDataCenters(respDC.data);
					const names = respDC.data.map((dc) => dc.name);
					setDataCenterOptions(names);

					if (names.length > 0) {
						setSelectedDataCenterName(names[0]);
					}
				}

				// 2) Types
				const respType = await getHardwareTypes(token);
				// Guardar la lista de {id, name}
				setTypeHardwareList(respType.data);
			} catch (error) {
				console.error("Error fetching data centers or hardware types:", error);
			} finally {
				setIsFetching(false);
			}
		})();
	}, [isLoading, isAuthenticated, getAccessTokenSilently]);

	// 3.2) Al cambiar DC => cargar hardware
	useEffect(() => {
		if (!selectedDataCenterName || !isAuthenticated) return;

		const foundDC = dataCenters.find(
			(dc) => dc.name === selectedDataCenterName
		);

		if (!foundDC) return;

		(async () => {
			try {
				setIsFetching(true);
				const token = await getAccessTokenSilently();
				const resp = await getHardwareByDatacenterId(
					token,
					foundDC.datacenterId
				);
				setHardwareList(resp.data || []);
				console.log("hardwareLis", resp.data);
			} catch (err) {
				console.error("Error fetching hardware by DC:", err);
			} finally {
				setIsFetching(false);
			}
		})();
	}, [
		selectedDataCenterName,
		dataCenters,
		isAuthenticated,
		getAccessTokenSilently,
	]);
	async function fetchHostnameOptions(dcId: string) {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getHardwareListByDataCenter(token, dcId);

			// Store the full objects for ID lookups
			setHostnameList(resp.data);
			// Also build the string[] for the dropdown
			setHostnameOptions(
				Array.from(
					new Set(resp.data.map((hw: HardwareDropdownItem) => hw.hostname))
				)
			);
		} catch (err) {
			console.error("Error fetching hostnames =>", err);
		} finally {
			setIsFetching(false);
		}
	}
	async function fetchSlotAssignment(hwId: number) {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getPortsByHardwareId(token, hwId.toString());
			// store the full list
			setSlotList(resp.data.ports || []);
			// build the string array
			setSlotOptions(
				resp.data.ports.map((p: PortItem) => `${p.portNumber} - ${p.portName}`)
			);
		} catch (err) {
			console.error("Error fetching slot assignments =>", err);
		} finally {
			setIsFetching(false);
		}
	}

	function handleSlotSelect(label: string) {
		const match = slotList.find(
			(p) => `${p.portNumber} - ${p.portName}` === label
		);
		if (!match) return;
		setSlotId(match.id);
	}
	function getSlotLabelById(id: number): string {
		const match = slotList.find((p) => p.id === id);
		return match ? `${match.portNumber} - ${match.portName}` : "";
	}

	// ===== 4) HANDLERS HARDWARE =====
	const handleDataCenterSelect = (selected: string) => {
		setSelectedDataCenterName(selected);
	};
	function handleHostnameSelect(chosenName: string) {
		const match = hostnameList.find((hw) => hw.hostname === chosenName);
		if (!match) return;
		setHostnameId(match.id); // Store the numeric hardware ID
		handleFieldChange("hostname", match.hostname);
		fetchSlotAssignment(match.id);
	}
	function getHostnameLabelById(hwId: number): string {
		const match = hostnameList.find((hw) => hw.id === hwId);
		return match ? match.hostname : "";
	}

	const handleAddHardware = () => {
		setEditMode(false);
		const newForm = { ...defaultHwForm };

		// If your user already picked a DC from the top-level dropdown,
		// find the matching data center object:
		const foundDC = dataCenters.find(
			(dc) => dc.name === selectedDataCenterName
		);
		if (foundDC) {
			// Put its ID in our form, so we can use it in the modal
			newForm.datacenterId = foundDC.datacenterId;
		}

		// Now set that form into state
		setHwForm(newForm);
		setSubTypeList([]); // Al crear, subType en blanco
		setIsModalOpen(true);
	};

	const handleEditHardware = (hw: Hardware) => {
		setEditMode(true);
		setHwForm({
			...defaultHwForm,
			id: String(hw.id),
			datacenterId: hw.datacenterId || "",
			typeHardware: hw.typeHardware,
			serialNumber: hw.serialNumber,
			model: hw.model,
			vendor: hw.vendor,
			hostname: hw.hostname,
			installedDate: hw.installedDate,
			endOfLifeDate: hw.endOfLifeDate,
			rackId: hw.rackId || "",
			rackUnit: hw.rackUnit ? String(hw.rackUnit) : "",
			status: hw.status,
			phase: hw.phase,
			template: hw.template || "",
			subtypeHardware: hw.subtypeHardware || "", // Por ahora vaciamos, a menos que tu API devuelva subType.
		});
		setSubTypeList([]);
		setIsModalOpen(true);
	};

	const handleViewHardware = (hw: Hardware) => {
		setSelectedHardware(hw);
		setIsViewModalOpen(true);
	};

	const handleDeleteClick = (hw: Hardware) => {
		setHardwareToDelete(hw);
		setIsAlertOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (!hardwareToDelete) return;

		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			await deleteHardwareResource(token, String(hardwareToDelete.id));
			setHardwareList((prev) =>
				prev.filter((item) => item.id !== hardwareToDelete.id)
			);
			setHardwareToDelete(null);
			setIsAlertOpen(false);
		} catch (err) {
			console.error("Error deleting hardware:", err);
		} finally {
			setIsFetching(false);
		}
	};

	const handleCancelDelete = () => {
		setHardwareToDelete(null);
		setIsAlertOpen(false);
	};

	// GUARDAR hardware (Add/Edit)
	const handleSaveHardware = async () => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();

			const { id, ...payload } = hwForm;

			if (hwForm.typeHardware === "Module") {
				payload.targetDeviceId = hostnameId;
				payload.slotAssignmentId = slotId;
			}

			// Si es un “Add”, asignar datacenter
			if (!editMode) {
				const foundDC = dataCenters.find(
					(dc) => dc.name === selectedDataCenterName
				);
				if (foundDC) {
					payload.datacenterId = foundDC.datacenterId;
				}
			}

			console.log("PAYLOAD", payload);

			if (editMode && id) {
				// EDIT
				await updateHardwareResource(token, id, payload);
				setHardwareList((prev) =>
					prev.map((item) =>
						item.id === Number(id) ? { ...item, ...payload } : item
					)
				);
			} else {
				// CREATE
				await addHardwareResource(token, payload);
				if (payload.datacenterId) {
					const r = await getHardwareByDatacenterId(
						token,
						payload.datacenterId
					);
					setHardwareList(r.data || []);
				}
			}
		} catch (err) {
			console.error("Error saving hardware resource:", err);
		} finally {
			setIsFetching(false);
			setIsModalOpen(false);
		}
	};

	// ===== 4.1) HANDLERS: Type & SubType =====
	const handleSelectType = async (
		selectedTypeName: string | { name: string }
	) => {
		let typeName: string;

		if (typeof selectedTypeName === "object") {
			// pick the .name field
			typeName = selectedTypeName.name;
		} else {
			typeName = selectedTypeName; // it's already a string
		}

		if (typeName === "Module" && hwForm.datacenterId) {
			fetchHostnameOptions(hwForm.datacenterId);
		}
		// Buscamos el ID del type a partir de su name
		const foundTypeObj = typeHardwareList.find(
			(t) => t.name === selectedTypeName
		);
		if (!foundTypeObj) {
			// No encontrado => sin subtypes
			setSubTypeList([]);
			return;
		}
		setHwForm((prev) => ({
			...prev,
			typeHardware: typeName,
			typeHardwareId: foundTypeObj?.id,
			subType: "",
		}));

		// Llamamos al endpoint GET /typeHardware/{id}/subtypes
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getSubTypesByTypeHardware(token, foundTypeObj.id);
			setSubTypeList(resp.data || []);
		} catch (error) {
			console.error("Error fetching subtypes =>", error);
			setSubTypeList([]);
		} finally {
			setIsFetching(false);
		}
	};

	function getTypeHardwareString(value: string | { name: string } | null) {
		if (!value) return "";
		if (typeof value === "object") return value.name;
		return value; // it's a string
	}

	const handleSelectSubType = (selectedSubType: string | { name: string }) => {
		let typeName: string;

		if (typeof selectedSubType === "object") {
			// pick the .name field
			typeName = selectedSubType.name;
		} else {
			typeName = selectedSubType; // it's already a string
		}
		// Find the selected subtype object
		const foundSubTypeObj = subTypeList.find((s) => s.name === typeName);
		setHwForm((prev) => ({
			...prev,
			subtypeHardware: typeName,
			subtypeHardwareId: foundSubTypeObj?.id ?? 0,
		}));
	};

	// ===== 5) HANDLERS: PUERTOS =====
	const handlePortsClick = async (hw: Hardware) => {
		try {
			setIsFetching(true);
			setSelectedHardwareForPorts(hw);
			setIsPortsModalOpen(true);
			setPortsList([]);

			const token = await getAccessTokenSilently();
			const resp = await getPortsByHardwareId(token, String(hw.id));

			if (resp.data && typeof resp.data === "object" && "ports" in resp.data) {
				setPortsList((resp.data as { ports: Port[] }).ports || []);
			} else {
				setPortsList(Array.isArray(resp.data) ? resp.data : []);
			}
		} catch (err) {
			console.error("Error fetching ports:", err);
		} finally {
			setIsFetching(false);
		}
	};

	const handleAddPort = () => {
		const newPort: Port = {
			id: `temp-${Date.now()}`,
			portName: "New Port",
			portNumber: "",
			portType: "TenGigE",
			portStatus: "available",
			isNew: true,
			isEditing: true,
		};
		setPortsList((prev) => [...prev, newPort]);
	};

	const handlePortFieldChange = (
		portId: string,
		field: keyof Port,
		value: string
	) => {
		setPortsList((prev) =>
			prev.map((p) => (p.id === portId ? { ...p, [field]: value } : p))
		);
	};

	const handleDeletePort = async (portId: string) => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();

			const found = portsList.find((p) => p.id === portId);
			if (!found) return;
			if (found.isNew) {
				// Solo lo quita local
				setPortsList((prev) => prev.filter((p) => p.id !== portId));
			} else {
				// Borrado real
				await deletePort(token, portId);
				if (selectedHardwareForPorts) {
					const resp = await getPortsByHardwareId(
						token,
						String(selectedHardwareForPorts.id)
					);
					if (
						resp.data &&
						typeof resp.data === "object" &&
						"ports" in resp.data
					) {
						setPortsList((resp.data as { ports: Port[] }).ports || []);
					} else {
						setPortsList(Array.isArray(resp.data) ? resp.data : []);
					}
				}
			}
		} catch (err) {
			console.error("Error deleting port =>", err);
		} finally {
			setIsFetching(false);
		}
	};

	const handleSavePorts = async () => {
		try {
			if (!selectedHardwareForPorts) return;
			setIsFetching(true);

			const hardwareIdNumber = selectedHardwareForPorts.id;
			const token = await getAccessTokenSilently();

			// Crear puertos “isNew”
			for (const port of portsList) {
				if (port.isNew) {
					await createPort(token, {
						hardwareResourceId: hardwareIdNumber,
						portName: port.portName,
						portId: port.id,
						portNumber: port.portNumber,
						portType: port.portType,
						portStatus: port.portStatus,
						l2Description: "",
						l3Description: "",
						clientAssignation: "",
					});
				}
			}
			// Refrescar
			const resp = await getPortsByHardwareId(token, String(hardwareIdNumber));
			if (resp.data && typeof resp.data === "object" && "ports" in resp.data) {
				setPortsList((resp.data as { ports: Port[] }).ports || []);
			} else {
				setPortsList(Array.isArray(resp.data) ? resp.data : []);
			}
			alert("Ports created successfully!");
		} catch (err) {
			console.error("Error creating ports =>", err);
		} finally {
			setIsFetching(false);
		}
	};

	const handleEditPort = (portId: string) => {
		setPortsList((prev) =>
			prev.map((p) => (p.id === portId ? { ...p, isEditing: true } : p))
		);
	};

	const handleUpdatePort = async (port: Port) => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			await updatePort(token, port.id, {
				portName: port.portName,
				portId: port.id,
				portNumber: port.portNumber,
				portType: port.portType,
				portStatus: port.portStatus,
				l2Description: "",
				l3Description: "",
				clientAssignation: "",
			});
			// Apagar edición
			setPortsList((prev) =>
				prev.map((p) => (p.id === port.id ? { ...p, isEditing: false } : p))
			);
			alert("Port updated!");
		} catch (error) {
			console.error("Error updating port =>", error);
		} finally {
			setIsFetching(false);
		}
	};

	// ===== 6) COLUMNAS DE HARDWARE =====
	const columns = getHardwareColumns({
		handleViewHardware,
		handleEditHardware,
		handleDeleteClick,
		handlePortsClick,
	});

	// Excluimos “type” y “subType” del form (mostramos manual en el modal)
	const hardwareFormFields = [
		{
			key: "serialNumber",
			label: "SerialNumber",
			placeholder: "e.g. FOC2625R1MM",
		},
		{ key: "model", label: "Model", placeholder: "e.g. NCS-5501-SE" },
		{ key: "vendor", label: "Vendor", placeholder: "e.g. CISCO" },
		{
			key: "hostname",
			label: "Hostname",
			placeholder: "e.g. DC001-ROUTER-PE1",
		},
		{
			key: "installedDate",
			label: "Installed Date",
			placeholder: "YYYY-MM-DD",
		},
		{ key: "endOfLifeDate", label: "EndOfLifeDate", placeholder: "YYYY-MM-DD" },
		{ key: "rackId", label: "RackId", placeholder: "e.g. RACK-001" },
		{ key: "rackUnit", label: "rackUnit", placeholder: "e.g. 46" },
		{ key: "status", label: "Status", placeholder: "Select status" },
		{ key: "phase", label: "Phase", placeholder: "e.g. 3-phase" },
		{ key: "template", label: "Template", placeholder: "e.g. NCS5501" },
	] as const;

	// ===== 7) DC Name read-only =====
	const currentDcName = useMemo(() => {
		if (editMode && hwForm.datacenterId) {
			const found = dataCenters.find(
				(dc) => dc.datacenterId === hwForm.datacenterId
			);
			return found ? found.name : "Unknown DC";
		}
		return selectedDataCenterName || "";
	}, [editMode, hwForm.datacenterId, dataCenters, selectedDataCenterName]);

	// Handler para cambiar campos con setHwForm
	const handleFieldChange = (
		fieldKey: keyof MyHardwareForm,
		newValue: string
	) => {
		setHwForm((prev) => ({ ...prev, [fieldKey]: newValue }));
	};

	// ===== 8) RENDER =====
	return (
		<div className="relative">
			{/* 1) SELECT DATACENTER */}
			<div className="mb-4 p-4 bg-[var(--semantic-bg-primary)] border border-semantic-border-secondary rounded-sm">
				<div className="flex items-end justify-between">
					<div className="flex items-end space-x-4">
						<div className="flex flex-col">
							<label className="mb-1 text-sm font-medium text-semantic-text-secondary">
								Select a Data Center
							</label>
							<GenericDropdown
								items={dataCenterOptions}
								defaultLabel="Choose DC..."
								value={selectedDataCenterName}
								onSelect={handleDataCenterSelect}
								minWidthClass="min-w-[200px]"
								maxWidthClass="max-w-[240px]"
							/>
						</div>
					</div>

					<GenericButton
						label={
							<div className="flex items-center gap-2">
								<IconPlus className="w-4 h-4" />
								<span>Add new hardware</span>
							</div>
						}
						variant="brand"
						size="md"
						onClick={handleAddHardware}
					/>
				</div>
			</div>

			{/* 2) TABLA HARDWARE */}
			<GenericTable
				columns={columns}
				data={hardwareList}
				width="100%"
				initialRowsPerPage={5}
				rowsPerPageOptions={[5, 10]}
				tableClassName="bg-semantic-bg-primary"
			/>

			{/* 3) MODAL => Add/Edit hardware */}
			<GenericModal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				title={editMode ? "Edit Hardware" : "Add Hardware"}
				size="lg"
			>
				<div className="grid grid-cols-3 gap-4 mb-4">
					{/* A) DataCenter read-only */}
					<GenericInput label="Data Center" value={currentDcName} readOnly />

					{/* B) TYPE => primer dropdown => set type => fetch subTypes */}
					<div className="flex flex-col">
						<label className="mb-1 text-sm font-medium text-semantic-text-secondary">
							Type
						</label>
						<GenericDropdown
							items={typeHardwareOptions}
							defaultLabel="Select Type"
							value={getTypeHardwareString(hwForm.typeHardware)}
							onSelect={handleSelectType}
							minWidthClass="min-w-[180px]"
							maxWidthClass="max-w-[240px]"
						/>
					</div>

					{/* C) SUBTYPE => segundo dropdown => depende del Type */}
					<div className="flex flex-col">
						<label className="mb-1 text-sm font-medium text-semantic-text-secondary">
							SubType
						</label>
						<GenericDropdown
							items={subTypeOptions}
							defaultLabel="Select SubType"
							value={getTypeHardwareString(hwForm.subtypeHardware)}
							onSelect={handleSelectSubType}
							minWidthClass="min-w-[180px]"
							maxWidthClass="max-w-[240px]"
						/>
					</div>

					{/* D) Campos del form => sin type ni subType */}
					{hardwareFormFields.map((field) => {
						// 1) Hide these fields if it's a "module"
						if (hwForm.typeHardware === "Module") {
							if (
								field.key === "phase" ||
								field.key === "rackUnit" ||
								field.key === "rackId" ||
								field.key === "template"
							) {
								return null; // Skip rendering
							}
						}

						// 2) If "status", render the status dropdown
						if (field.key === "status") {
							return (
								<div className="flex flex-col" key={field.key}>
									<label className="mb-1 text-sm font-medium text-semantic-text-secondary">
										{field.label}
									</label>
									<GenericDropdown
										items={["Installed", "Assigned"]}
										value={hwForm.status ?? ""}
										defaultLabel="Select Status"
										onSelect={(val) => handleFieldChange("status", val)}
										minWidthClass="min-w-[180px]"
										maxWidthClass="max-w-[240px]"
									/>
								</div>
							);
						}

						// 3) If "hostname", render the hostname dropdown
						if (field.key === "hostname") {
							return (
								<div className="flex flex-col " key={field.key}>
									<label className="mb-1 text-sm font-medium text-semantic-text-secondary">
										{field.label}
									</label>
									<GenericDropdown
										items={hostnameOptions} // e.g. from father approach
										value={hostnameId ? getHostnameLabelById(hostnameId) : ""}
										defaultLabel="Select Hostname..."
										onSelect={handleHostnameSelect}
										minWidthClass="min-w-[180px]"
										maxWidthClass="max-w-[240px]"
									/>

									<div className="flex flex-col mt-4">
										<label className="mb-1 text-sm font-medium text-semantic-text-secondary">
											Slot Assignment
										</label>
										<GenericDropdown
											items={slotOptions}
											value={slotId ? getSlotLabelById(slotId) : ""}
											onSelect={handleSlotSelect}
											disabled={!hostnameId}
											defaultLabel="Select Slot..."
											minWidthClass="min-w-[180px]"
											maxWidthClass="max-w-[240px]"
										/>
									</div>
								</div>
							);
						}

						// 4) Otherwise, render a normal GenericInput
						return (
							<GenericInput
								key={field.key}
								label={field.label}
								placeholder={field.placeholder}
								value={hwForm[field.key] ?? ""}
								onChange={(val: string) => handleFieldChange(field.key, val)}
							/>
						);
					})}
				</div>

				<div className="flex justify-end space-x-2">
					<button
						className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
						onClick={() => setIsModalOpen(false)}
					>
						Cancel
					</button>
					<button
						className="
              px-4 py-2
              text-sm
              font-medium
              text-white
              bg-semantic-text-brand
              rounded
              hover:bg-opacity-90
              transition-colors
            "
						onClick={handleSaveHardware}
					>
						Save
					</button>
				</div>
			</GenericModal>

			{/* 4) MODAL => View hardware */}
			<GenericModal
				isOpen={isViewModalOpen}
				onClose={() => setIsViewModalOpen(false)}
				title="Hardware Details"
				size="lg"
			>
				<div className="grid grid-cols-3 gap-4 mb-4">
					<GenericInput
						label="ID"
						value={String(selectedHardware?.id ?? "")}
						readOnly
					/>
					<GenericInput
						label="datacenterId"
						value={selectedHardware?.datacenterId ?? ""}
						readOnly
					/>
					<GenericInput
						label="Type"
						value={getTypeHardwareString(selectedHardware?.typeHardware ?? "")}
						readOnly
					/>
					<GenericInput
						label="SubType"
						value={getTypeHardwareString(selectedHardware?.subtypeHardware ?? "")}
						readOnly
					/>
					<GenericInput
						label="SerialNumber"
						value={selectedHardware?.serialNumber ?? ""}
						readOnly
					/>
					<GenericInput
						label="Model"
						value={selectedHardware?.model ?? ""}
						readOnly
					/>
					<GenericInput
						label="Vendor"
						value={selectedHardware?.vendor ?? ""}
						readOnly
					/>
					<GenericInput
						label="Hostname"
						value={selectedHardware?.hostname ?? ""}
						readOnly
					/>
					<GenericInput
						label="Installed Date"
						value={selectedHardware?.installedDate ?? ""}
						readOnly
					/>
					<GenericInput
						label="EndOfLifeDate"
						value={selectedHardware?.endOfLifeDate ?? ""}
						readOnly
					/>

					{selectedHardware?.typeHardware !== "Module" && (
						<>
							<GenericInput
								label="RackId"
								value={selectedHardware?.rackId ?? ""}
								readOnly
							/>
							<GenericInput
								label="Rack Unit"
								value={
									selectedHardware?.rackUnit !== undefined
										? String(selectedHardware.rackUnit)
										: ""
								}
								readOnly
							/>
							<GenericInput
								label="Phase"
								value={selectedHardware?.phase ?? ""}
								readOnly
							/>
							<GenericInput
								label="Template"
								value={selectedHardware?.template ?? ""}
								readOnly
							/>
						</>
					)}
				</div>

				<div className="flex justify-end">
					<button
						className="
              px-4 py-2
              text-sm
              font-medium
              text-semantic-text-secondary
              bg-semantic-bg-primary
              border border-semantic-border-secondary
              rounded
              hover:bg-semantic-state-active
              transition-colors
            "
						onClick={() => setIsViewModalOpen(false)}
					>
						Close
					</button>
				</div>
			</GenericModal>

			{/* 5) ALERT => confirm Delete hardware */}
			<GenericAlert
				isOpen={isAlertOpen}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="Are you sure?"
				description="This will permanently delete the hardware from the inventory."
				cancelLabel="Cancel"
				confirmLabel="Delete"
			/>

			{/* 6) MODAL => Ports */}
			<GenericModal
				isOpen={isPortsModalOpen}
				onClose={() => setIsPortsModalOpen(false)}
				title={`Ports for ${selectedHardwareForPorts?.name || ""}`}
				size="lg"
			>
				<div className="mb-4">
					<GenericButton
						label="Add Port"
						variant="brand"
						size="md"
						onClick={handleAddPort}
					/>
				</div>

				<div style={{ maxHeight: "60vh", overflowY: "auto" }}>
					<table className="table-auto w-full text-sm text-left">
						<thead className="sticky top-0 bg-white border-b z-10">
							<tr>
								<th className="px-4 py-2">Port Name</th>
								<th className="px-4 py-2">Port ID</th>
								<th className="px-4 py-2">Port Number</th>
								<th className="px-4 py-2">Port Type</th>
								<th className="px-4 py-2">Port Status</th>
								<th className="px-4 py-2">Actions</th>
							</tr>
						</thead>
						<tbody>
							{portsList.map((port) => {
								const isEditable = port.isNew || port.isEditing;
								return (
									<tr key={port.id} className="border-b hover:bg-gray-100">
										<td className="px-4 py-2">
											{isEditable ? (
												<input
													className="border rounded px-2"
													value={port.portName}
													onChange={(e) =>
														handlePortFieldChange(
															port.id,
															"portName",
															e.target.value
														)
													}
												/>
											) : (
												port.portName
											)}
										</td>
										<td className="px-4 py-2">
											{isEditable ? (
												<input
													className="border rounded px-2"
													value={port.id}
													onChange={(e) =>
														handlePortFieldChange(port.id, "id", e.target.value)
													}
												/>
											) : (
												port.id
											)}
										</td>
										<td className="px-4 py-2">
											{isEditable ? (
												<input
													className="border rounded px-2"
													value={port.portNumber}
													onChange={(e) =>
														handlePortFieldChange(
															port.id,
															"portNumber",
															e.target.value
														)
													}
												/>
											) : (
												port.portNumber
											)}
										</td>
										<td className="px-4 py-2">
											{isEditable ? (
												<input
													className="border rounded px-2"
													value={port.portType}
													onChange={(e) =>
														handlePortFieldChange(
															port.id,
															"portType",
															e.target.value
														)
													}
												/>
											) : (
												port.portType
											)}
										</td>
										<td className="px-4 py-2">
											{isEditable ? (
												<input
													className="border rounded px-2"
													value={port.portStatus}
													onChange={(e) =>
														handlePortFieldChange(
															port.id,
															"portStatus",
															e.target.value
														)
													}
												/>
											) : (
												port.portStatus
											)}
										</td>
										<td className="px-4 py-2 flex gap-2">
											<button
												className="bg-red-600 text-white px-2 py-1 rounded"
												onClick={() => handleDeletePort(port.id)}
											>
												Delete
											</button>
											{!port.isNew && !port.isEditing && (
												<button
													className="bg-blue-600 text-white px-2 py-1 rounded"
													onClick={() => handleEditPort(port.id)}
												>
													Update
												</button>
											)}
											{port.isEditing && !port.isNew && (
												<button
													className="bg-green-600 text-white px-2 py-1 rounded"
													onClick={() => handleUpdatePort(port)}
												>
													Save
												</button>
											)}
										</td>
									</tr>
								);
							})}

							{portsList.length === 0 && (
								<tr>
									<td
										colSpan={6}
										className="px-4 py-3 text-center text-gray-500"
									>
										No ports found for this hardware
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<div className="flex justify-end mt-4 gap-2">
					<button
						className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded hover:bg-gray-700"
						onClick={() => setIsPortsModalOpen(false)}
					>
						Close
					</button>
					<button
						className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-500"
						onClick={handleSavePorts}
					>
						Save New Ports
					</button>
				</div>
			</GenericModal>

			{/* 7) Overlay => spinner */}
			{isFetching && (
				<div
					className="
            fixed
            inset-0
            flex flex-col items-center justify-center
            bg-black/50
            z-[9999]
          "
				>
					<h2 className="text-xl font-semibold mb-4 text-white">
						Processing request...
					</h2>
					<div
						className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4
              border-gray-300
              border-t-blue-500
            "
					/>
				</div>
			)}
		</div>
	);
}
